import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/hooks/use-auth";
import { formatPrice } from "@/lib/format";
import { supabase } from "@/integrations/supabase/client";
import { placeOrder } from "@/lib/orders.functions";

export const Route = createFileRoute("/checkout")({
  head: () => ({ meta: [{ title: "Checkout — Jul's Skin" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: CheckoutPage,
});

// Replace with your real UPI ID later (e.g. brand@okhdfcbank)
const UPI_ID = "atelier@upi";
const UPI_NAME = "Jul,s Skin";

function CheckoutPage() {
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const placeOrderFn = useServerFn(placeOrder);

  const [form, setForm] = useState({
    full_name: "", email: user?.email ?? "", phone: "",
    address_line1: "", address_line2: "", city: "", state: "", pincode: "",
  });
  const [step, setStep] = useState<"details" | "pay">("details");
  const [orderId, setOrderId] = useState<string | null>(null);
  const [qr, setQr] = useState<string>("");
  const [upiRef, setUpiRef] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (user?.email && !form.email) setForm((f) => ({ ...f, email: user.email! })); }, [user]);

  if (items.length === 0 && step === "details") {
    return (
      <div className="min-h-screen flex flex-col">
        <SiteHeader />
        <div className="mx-auto max-w-md w-full px-6 py-24 text-center">
          <h1 className="display text-3xl">Nothing to check out</h1>
          <Link to="/shop" className="mt-6 inline-block underline">Continue shopping</Link>
        </div>
        <div className="flex-1" /><SiteFooter />
      </div>
    );
  }

  const handlePlaceOrder = async () => {
    if (!user) { toast.error("Please sign in to place an order"); navigate({ to: "/auth", search: { redirect: "/checkout" } }); return; }
    for (const k of ["full_name","phone","address_line1","city","state","pincode"] as const) {
      if (!form[k].trim()) { toast.error("Please fill in all required fields"); return; }
    }
    setSubmitting(true);
    try {
      const order = await placeOrderFn({
        data: {
          full_name: form.full_name,
          email: form.email,
          phone: form.phone,
          address_line1: form.address_line1,
          address_line2: form.address_line2 || null,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
          items: items.map((i) => ({
            productId: i.productId,
            size: i.size,
            quantity: i.quantity,
          })),
        },
      });

      const amount = (order.total_cents / 100).toFixed(2);
      const upiLink = `upi://pay?pa=${encodeURIComponent(UPI_ID)}&pn=${encodeURIComponent(UPI_NAME)}&am=${amount}&cu=INR&tn=${encodeURIComponent("Order " + order.id.slice(0,8))}`;
      const qrUrl = await QRCode.toDataURL(upiLink, { margin: 1, width: 320, color: { dark: "#1a1a1a", light: "#f5f3ee" } });
      setQr(qrUrl); setOrderId(order.id); setStep("pay");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not place order");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmPayment = async () => {
    if (!orderId) return;
    const { error } = await supabase.from("orders").update({ upi_reference: upiRef || null }).eq("id", orderId);
    if (error) { toast.error(error.message); return; }
    toast.success("Payment marked as sent. We'll verify and confirm shortly.");
    clear();
    navigate({ to: "/orders" });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <div className="mx-auto max-w-5xl w-full px-6 py-16">
        <h1 className="display text-4xl md:text-5xl">Checkout</h1>

        {step === "details" ? (
          <div className="mt-12 grid lg:grid-cols-[1fr_360px] gap-12">
            <div className="space-y-6">
              <p className="eyebrow">Shipping details</p>
              {!user && (
                <div className="border border-border p-4 text-sm">
                  <Link to="/auth" search={{ redirect: "/checkout" }} className="underline">Sign in</Link> to track your order.
                </div>
              )}
              <Field label="Full name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              </div>
              <Field label="Address line 1" value={form.address_line1} onChange={(v) => setForm({ ...form, address_line1: v })} />
              <Field label="Address line 2 (optional)" value={form.address_line2} onChange={(v) => setForm({ ...form, address_line2: v })} />
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
                <Field label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
                <Field label="PIN code" value={form.pincode} onChange={(v) => setForm({ ...form, pincode: v })} />
              </div>
              <button disabled={submitting} onClick={handlePlaceOrder} className="bg-primary text-primary-foreground px-8 py-4 text-sm hover:opacity-90 disabled:opacity-50">
                {submitting ? "Placing order…" : "Continue to payment"}
              </button>
            </div>

            <aside className="border border-border p-6 h-fit space-y-4">
              <p className="eyebrow">Order</p>
              <ul className="space-y-3 text-sm">
                {items.map((i) => (
                  <li key={i.productId + i.size} className="flex justify-between gap-3">
                    <span className="text-muted-foreground">{i.name} × {i.quantity} <span className="opacity-60">/ {i.size}</span></span>
                    <span className="tabular-nums">{formatPrice(i.price_cents * i.quantity)}</span>
                  </li>
                ))}
              </ul>
              <div className="hairline pt-4 flex justify-between text-base"><span>Total</span><span className="tabular-nums">{formatPrice(total)}</span></div>
            </aside>
          </div>
        ) : (
          <div className="mt-12 max-w-lg mx-auto text-center border border-border p-10">
            <p className="eyebrow text-muted-foreground">Pay via UPI</p>
            <h2 className="display text-3xl mt-2">Scan to pay {formatPrice(total)}</h2>
            <p className="text-sm text-muted-foreground mt-2">Open any UPI app (GPay, PhonePe, Paytm, BHIM) and scan</p>
            {qr && <img src={qr} alt="UPI QR code" className="mx-auto mt-8 w-72 h-72 border border-border" />}
            <div className="mt-6 text-sm space-y-1">
              <p>UPI ID: <span className="font-mono">{UPI_ID}</span></p>
              <p>Order: <span className="font-mono">{orderId?.slice(0, 8)}</span></p>
            </div>
            <div className="mt-8 text-left">
              <label className="eyebrow block mb-2">UPI Transaction ID (after paying)</label>
              <input value={upiRef} onChange={(e) => setUpiRef(e.target.value)} className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:border-foreground" placeholder="e.g. 432109876543" />
            </div>
            <button onClick={confirmPayment} className="mt-6 w-full bg-primary text-primary-foreground py-4 text-sm hover:opacity-90">
              I've paid — submit
            </button>
            <p className="text-xs text-muted-foreground mt-4">We'll verify the payment and confirm your order via email.</p>
          </div>
        )}
      </div>
      <div className="flex-1" /><SiteFooter />
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
    </label>
  );
}
